/**
 * Iter276-audit — Materials Audit Console
 *
 * Left column  : component list with issue-count badges.
 * Right column : issue-first table of catalogue rows for the selected
 *                component. Three-way filter (All / Un-audited / Audited).
 *                Inline fix popover, sample calc breakdown, PDF preview,
 *                bulk supplier assign, CSV export, auto-unit suggestions.
 *
 * Route: /admin/materials-audit
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, AlertCircle, Info, Check, Loader2, RefreshCw,
  FileDown, Wrench, X, Search, Download, ChevronRight, Sparkles, ExternalLink,
  ChefHat,
} from 'lucide-react';import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { SupplierEditModal } from '@/pages/SuppliersAdminPage';
import RecipeEditorPage from '@/pages/RecipeEditorPage';

const SEV_META = {
  error: { icon: AlertCircle,   klass: 'text-rose-600 bg-rose-50 border-rose-200',       label: 'Error' },
  warn:  { icon: AlertTriangle, klass: 'text-amber-700 bg-amber-50 border-amber-200',    label: 'Warn'  },
  info:  { icon: Info,          klass: 'text-sky-700 bg-sky-50 border-sky-200',          label: 'Info'  },
};

const FILTER_ALL = 'all';
const FILTER_UNAUDITED = 'unaudited';
const FILTER_AUDITED = 'audited';

export default function MaterialsAuditPage() {
  const { api } = useAuth();
  const [scan, setScan] = useState(null);
  const [scanLoading, setScanLoading] = useState(true);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [filter, setFilter] = useState(FILTER_UNAUDITED);
  const [needsRecipeOnly, setNeedsRecipeOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [checkedRows, setCheckedRows] = useState(new Set());

  const [editingRow, setEditingRow] = useState(null);      // row object being edited
  const [previewRow, setPreviewRow] = useState(null);      // row object being calc-previewed
  const [bulkOpen, setBulkOpen] = useState(false);
  const [autoUnit, setAutoUnit] = useState(null);          // suggestion payload

  const doScan = useCallback(async () => {
    setScanLoading(true);
    try {
      const { data } = await api.get('/materials-audit/scan');
      setScan(data);
      // Preserve selection if still present, otherwise first component.
      if (data.components?.length) {
        const still = selectedComponent && data.components.find((c) => c.component === selectedComponent);
        setSelectedComponent(still ? still.component : data.components[0].component);
      }
    } catch (err) {
      toast.error(`Scan failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setScanLoading(false);
    }
  }, [api, selectedComponent]);
  useEffect(() => { doScan(); }, []);

  const componentBucket = useMemo(
    () => scan?.components?.find((c) => c.component === selectedComponent),
    [scan, selectedComponent],
  );
  // Group ordering for the Recoat component — mirrors the Paint
  // Systems Catalogue: Roof recoats → Gutters/Fascia → Garage
  // Doors → Fencing → uncategorised.
  const recoatGroupOf = (name) => {
    const n = (name || '').toLowerCase();
    if (/(gutter|fascia)/.test(n))       return 2;
    if (/garage/.test(n))                return 3;
    if (/fence/.test(n))                 return 4;
    if (/(concrete|iron|clay|colortile|decramastic|chipped|long.?run|premium|old|modern)/.test(n)) return 1;
    return 5;
  };

  const visibleRows = useMemo(() => {
    if (!componentBucket) return [];
    const q = search.trim().toLowerCase();
    const filtered = componentBucket.rows.filter((r) => {
      if (filter === FILTER_UNAUDITED && r.last_audited_at) return false;
      if (filter === FILTER_AUDITED && !r.last_audited_at) return false;
      if (needsRecipeOnly && !r.needs_recipe) return false;
      if (q && !(r.name || '').toLowerCase().includes(q) && !(r.materials_desc || '').toLowerCase().includes(q)) return false;
      return true;
    });
    if (componentBucket.component === 'recoat') {
      filtered.sort((a, b) => {
        const ga = recoatGroupOf(a.name), gb = recoatGroupOf(b.name);
        if (ga !== gb) return ga - gb;
        return (a.name || '').localeCompare(b.name || '');
      });
    }
    return filtered;
  }, [componentBucket, filter, needsRecipeOnly, search]);

  const toggleCheck = (rowId) => {
    setCheckedRows((prev) => {
      const next = new Set(prev);
      next.has(rowId) ? next.delete(rowId) : next.add(rowId);
      return next;
    });
  };
  const clearChecks = () => setCheckedRows(new Set());

  // ─── Actions ───
  const markAudited = async (row) => {
    try {
      await api.post(`/materials-audit/rows/${row._id}/mark-audited`);
      toast.success('Marked audited');
      doScan();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };
  const unmarkAudited = async (row) => {
    try {
      await api.delete(`/materials-audit/rows/${row._id}/mark-audited`);
      toast.success('Unmarked');
      doScan();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };
  const bulkMarkAudited = async (rowIds) => {
    if (!rowIds?.length) return;
    if (!window.confirm(`Mark ${rowIds.length} rows as audited?\n\nUse this only after you've visually confirmed each row's fields are correct.`)) return;
    try {
      const { data } = await api.post('/materials-audit/bulk-mark-audited', { row_ids: rowIds });
      toast.success(`${data.marked} rows marked audited`);
      clearChecks();
      doScan();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };
  const toggleNeedsRecipe = async (row) => {
    try {
      if (row.needs_recipe) {
        await api.delete(`/materials-audit/rows/${row._id}/needs-recipe`);
        toast.success('Unmarked "needs recipe"');
      } else {
        await api.post(`/materials-audit/rows/${row._id}/needs-recipe`);
        toast.success('Marked as needing a recipe');
      }
      doScan();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };
  const deleteRow = async (row) => {
    if (!window.confirm(
      `Delete "${row.name}" from ${row.component}?\n\n` +
      `This removes the catalogue row, its rate mirror, and any attached recipe. ` +
      `Rows still referenced by live quotes cannot be deleted.`,
    )) return;
    try {
      const { data } = await api.delete(`/materials-audit/rows/${row._id}`);
      const d = data.deleted || {};
      toast.success(
        `Deleted · CRT ${d.component_rate_tables} · CR mirror ${d.component_rates} · recipe ${d.material_recipes}`,
      );
      doScan();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };
  const loadAutoUnit = async () => {
    try {
      const { data } = await api.get('/materials-audit/auto-unit-suggestions');
      setAutoUnit(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };
  useEffect(() => { if (scan) loadAutoUnit(); }, [scan]);

  const exportCsv = async () => {
    try {
      const resp = await api.get('/materials-audit/export.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'materials-audit.csv'; document.body.appendChild(a);
      a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="p-4" data-testid="materials-audit-page">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Wrench className="text-orange-600" size={20} />
            Materials Audit Console
          </h1>
          <p className="text-xs text-slate-500">
            Walk through each component. Fix issues in place. Mark rows audited when the order-sheet output looks right.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={doScan}
            disabled={scanLoading}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
            data-testid="materials-audit-rescan-btn"
          >
            {scanLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Re-scan
          </button>
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
            data-testid="materials-audit-export-csv"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      {scanLoading && !scan ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 size={20} className="animate-spin mr-2" /> Scanning catalogue…
        </div>
      ) : !scan ? null : (
        <div className="grid grid-cols-[220px_1fr] gap-4">
          {/* ─── LEFT · component list ─── */}
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="materials-audit-component-list">
            <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold border-b border-slate-100 bg-slate-50">
              Components ({scan.components.length})
            </div>
            {(() => {
              const totals = scan.components.reduce(
                (acc, c) => ({
                  rows:    acc.rows + (c.total_rows || 0),
                  audited: acc.audited + (c.audited_count || 0),
                  needs:   acc.needs + (c.rows || []).filter((r) => r.needs_recipe).length,
                }),
                { rows: 0, audited: 0, needs: 0 },
              );
              const pct = totals.rows > 0 ? Math.round((totals.audited / totals.rows) * 100) : 0;
              const barColor = pct >= 90 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : pct > 0 ? 'bg-rose-400' : 'bg-slate-300';
              return (
                <div className="px-3 py-2 border-b border-slate-100 bg-slate-50" data-testid="materials-audit-sidebar-overall">
                  <div className="flex items-center justify-between text-[10px] text-slate-600">
                    <span className="uppercase tracking-wider font-bold">Catalogue progress</span>
                    <span className={`tabular-nums font-bold ${pct === 100 ? 'text-emerald-600' : 'text-slate-700'}`} data-testid="materials-audit-sidebar-overall-pct">{pct}%</span>
                  </div>
                  <div className="mt-1 h-1.5 w-full bg-white rounded-full overflow-hidden border border-slate-200">
                    <div className={`h-full ${barColor} transition-all duration-300`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="mt-1 text-[9px] text-slate-500 tabular-nums">
                    {totals.audited.toLocaleString()} / {totals.rows.toLocaleString()} rows audited{totals.needs > 0 && <> · <span className="text-amber-700 font-semibold">{totals.needs} 🍳 flagged</span></>}
                  </div>
                </div>
              );
            })()}
            <div className="max-h-[70vh] overflow-y-auto">
              {scan.components.map((c) => {
                const active = c.component === selectedComponent;
                const clean = c.issues_count === 0;
                const pct = c.total_rows > 0 ? Math.round((c.audited_count / c.total_rows) * 100) : 0;
                const nrCount = (c.rows || []).filter((r) => r.needs_recipe).length;
                // Colour transitions: rose → amber → emerald as coverage grows.
                const barColor = pct >= 90 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : pct > 0 ? 'bg-rose-400' : 'bg-slate-300';
                return (
                  <button
                    key={c.component}
                    onClick={() => { setSelectedComponent(c.component); clearChecks(); }}
                    data-testid={`materials-audit-component-${c.component}`}
                    className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50 ${active ? 'bg-orange-50 border-l-4 border-l-orange-500' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`text-xs font-medium ${active ? 'text-orange-900' : 'text-slate-800'} capitalize`}>{c.component}</div>
                        <div className="text-[10px] text-slate-500">
                          {c.total_rows} rows · <span className={clean ? 'text-emerald-600' : 'text-rose-600 font-bold'}>{c.issues_count} issue{c.issues_count === 1 ? '' : 's'}</span>
                          {c.audited_count > 0 && <> · <span className="text-emerald-600">{c.audited_count} ✓</span></>}
                          {nrCount > 0 && <> · <span className="text-amber-700 font-semibold">{nrCount} 🍳</span></>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[10px] tabular-nums font-bold ${pct === 100 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-700' : 'text-slate-500'}`} data-testid={`materials-audit-component-${c.component}-pct`}>{pct}%</span>
                        {clean ? <Check size={14} className="text-emerald-600" /> : <ChevronRight size={12} className="text-slate-400" />}
                      </div>
                    </div>
                    <div className="mt-1.5 h-1 w-full bg-slate-100 rounded-full overflow-hidden" title={`${c.audited_count} of ${c.total_rows} rows audited`}>
                      <div
                        className={`h-full ${barColor} transition-all duration-300`}
                        style={{ width: `${pct}%` }}
                        data-testid={`materials-audit-component-${c.component}-bar`}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ─── RIGHT · component detail ─── */}
          <div className="bg-white border border-slate-200 rounded-md">
            {!componentBucket ? (
              <div className="p-8 text-center text-slate-500 text-sm">Pick a component from the left to start.</div>
            ) : (
              <>
                <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-sm font-bold text-slate-900 capitalize flex items-center gap-2">
                      {componentBucket.component}
                      <Link
                        to={`/component-rates?component=${componentBucket.component}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`materials-audit-open-rate-table-${componentBucket.component}`}
                        title="Open the Component Rates Table for this component in a new tab"
                        className="text-[10px] font-normal normal-case tracking-normal text-sky-700 hover:text-sky-900 hover:underline inline-flex items-center gap-0.5"
                      >
                        <ExternalLink size={10} /> Rate Table
                      </Link>
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {componentBucket.total_rows} rows · {componentBucket.issues_count} with issues · {componentBucket.audited_count} audited
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {[FILTER_ALL, FILTER_UNAUDITED, FILTER_AUDITED].map((f) => (
                      <button
                        key={f}
                        onClick={() => setFilter(f)}
                        data-testid={`materials-audit-filter-${f}`}
                        className={`text-[10px] px-2 py-1 rounded-md border font-bold uppercase tracking-wider ${filter === f ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                      >
                        {f === FILTER_ALL ? `All (${componentBucket.total_rows})` : f === FILTER_UNAUDITED ? `Un-audited (${componentBucket.total_rows - componentBucket.audited_count})` : `Audited (${componentBucket.audited_count})`}
                      </button>
                    ))}
                    {(() => {
                      const nrCount = componentBucket.rows.filter((r) => r.needs_recipe).length;
                      if (nrCount === 0 && !needsRecipeOnly) return null;
                      return (
                        <button
                          onClick={() => setNeedsRecipeOnly((v) => !v)}
                          data-testid="materials-audit-filter-needs-recipe"
                          title="Filter to rows flagged as needing a recipe"
                          className={`text-[10px] px-2 py-1 rounded-md border font-bold uppercase tracking-wider inline-flex items-center gap-1 ${needsRecipeOnly ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-800 border-amber-300 hover:bg-amber-50'}`}
                        >
                          <ChefHat size={10} /> Needs recipe ({nrCount})
                        </button>
                      );
                    })()}
                  </div>
                </div>
                <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 flex-1 min-w-[200px] max-w-md relative">
                    <Search size={12} className="absolute left-2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search by row name or description…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      data-testid="materials-audit-search"
                      className="w-full text-xs border border-slate-300 rounded-md pl-6 pr-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400"
                    />
                  </div>
                  {checkedRows.size > 0 && (
                    <>
                      <button
                        onClick={() => setBulkOpen(true)}
                        data-testid="materials-audit-bulk-supplier-btn"
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100"
                      >
                        Bulk assign supplier ({checkedRows.size})
                      </button>
                      <button
                        onClick={() => bulkMarkAudited(Array.from(checkedRows))}
                        data-testid="materials-audit-bulk-mark-audited-btn"
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                      >
                        Mark {checkedRows.size} audited
                      </button>
                      <button
                        onClick={clearChecks}
                        data-testid="materials-audit-clear-checks"
                        className="text-[10px] px-1.5 py-0.5 rounded text-slate-500 hover:text-slate-800"
                      >
                        clear
                      </button>
                    </>
                  )}
                  {(() => {
                    const unaudited = visibleRows.filter((r) => !r.last_audited_at);
                    if (unaudited.length < 2 || checkedRows.size > 0) return null;
                    return (
                      <button
                        onClick={() => setCheckedRows(new Set(unaudited.map((r) => r._id)))}
                        data-testid="materials-audit-select-all-unaudited"
                        title="Select every un-audited row in this section"
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                      >
                        Select all un-audited ({unaudited.length})
                      </button>
                    );
                  })()}
                </div>
                {autoUnit?.suggestions?.some((s) => s.component === componentBucket.component) && (
                  <AutoUnitBanner
                    suggestion={autoUnit.suggestions.find((s) => s.component === componentBucket.component)}
                  />
                )}
                {['sheet', 'barge', 'ridge', 'valley', 'flashing'].includes(componentBucket.component) && (
                  <AutoFillPanel
                    api={api}
                    component={componentBucket.component}
                    onApplied={() => doScan()}
                  />
                )}

                {/* Table */}
                <div className="max-h-[65vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                        <th className="px-2 py-2 text-left w-6">
                          {(() => {
                            const visibleIds = visibleRows.map((r) => r._id);
                            const allChecked = visibleIds.length > 0 && visibleIds.every((id) => checkedRows.has(id));
                            const someChecked = visibleIds.some((id) => checkedRows.has(id));
                            return (
                              <input
                                type="checkbox"
                                checked={allChecked}
                                ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setCheckedRows(new Set([...checkedRows, ...visibleIds]));
                                  } else {
                                    const next = new Set(checkedRows);
                                    visibleIds.forEach((id) => next.delete(id));
                                    setCheckedRows(next);
                                  }
                                }}
                                data-testid="materials-audit-select-all-header"
                                title={allChecked ? 'Clear selection for this section' : 'Select every visible row in this section'}
                              />
                            );
                          })()}
                        </th>
                        <th className="px-2 py-2 text-left">Row</th>
                        <th className="px-2 py-2 text-left">Supplier</th>
                        <th className="px-2 py-2 text-left">Code</th>
                        <th className="px-2 py-2 text-right">Rate</th>
                        <th className="px-2 py-2 text-left">Unit</th>
                        <th className="px-2 py-2 text-right">Ratio</th>
                        <th className="px-2 py-2 text-left">Issues</th>
                        <th className="px-2 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.length === 0 ? (
                        <tr><td colSpan={9} className="text-center text-slate-400 py-8 text-xs italic">No rows match this filter.</td></tr>
                      ) : (() => {
                        // For Spouting, group rows by meta.gutter_type with sub-header dividers.
                        const isSpouting = componentBucket.component === 'spouting';
                        if (!isSpouting) {
                          return visibleRows.map((r) => (
                            <RowLine
                              key={r._id}
                              row={r}
                              checked={checkedRows.has(r._id)}
                              onCheck={() => toggleCheck(r._id)}
                              onEdit={() => setEditingRow(r)}
                              onPreview={() => setPreviewRow(r)}
                              onMarkAudited={() => markAudited(r)}
                              onUnmarkAudited={() => unmarkAudited(r)}
                              onToggleNeedsRecipe={() => toggleNeedsRecipe(r)}
                              onDelete={() => deleteRow(r)}
                            />
                          ));
                        }
                        // Group order for Spouting
                        const GROUP_ORDER = [
                          'Gutter 125 Square',
                          'Gutter 175 Industrial 0.55',
                          'Gutter 300 Industrial 0.55',
                          'Gutter Quarter Round',
                          'Rainhead',
                          'Marley Gutter',
                          'Downpipe Round 65mm',
                          'Downpipe Round 80mm',
                          'Downpipe Round 100mm',
                          'Downpipe Rectangle 50×100mm',
                          'Downpipe Labour',
                          'Misc',
                        ];
                        const buckets = new Map();
                        for (const r of visibleRows) {
                          const gt = (r.meta && r.meta.gutter_type) || 'Unclassified';
                          if (!buckets.has(gt)) buckets.set(gt, []);
                          buckets.get(gt).push(r);
                        }
                        const orderedKeys = [
                          ...GROUP_ORDER.filter((k) => buckets.has(k)),
                          ...[...buckets.keys()].filter((k) => !GROUP_ORDER.includes(k)),
                        ];
                        const elements = [];
                        for (const gt of orderedKeys) {
                          const groupRows = buckets.get(gt);
                          elements.push(
                            <tr key={`hdr-${gt}`} className="bg-teal-50/70 border-t border-b border-teal-200" data-testid={`materials-audit-group-header-${gt.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                              <td colSpan={9} className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-teal-800 font-bold">
                                <span className="inline-block bg-teal-200 text-teal-900 rounded px-1.5 py-0.5 mr-2 tabular-nums">{groupRows.length}</span>
                                {gt}
                              </td>
                            </tr>
                          );
                          for (const r of groupRows) {
                            elements.push(
                              <RowLine
                                key={r._id}
                                row={r}
                                checked={checkedRows.has(r._id)}
                                onCheck={() => toggleCheck(r._id)}
                                onEdit={() => setEditingRow(r)}
                                onPreview={() => setPreviewRow(r)}
                                onMarkAudited={() => markAudited(r)}
                                onUnmarkAudited={() => unmarkAudited(r)}
                                onToggleNeedsRecipe={() => toggleNeedsRecipe(r)}
                                onDelete={() => deleteRow(r)}
                              />
                            );
                          }
                        }
                        return elements;
                      })()}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {editingRow && (
        <EditRowModal api={api} row={editingRow} availableComponents={(scan?.components || []).map(c => c.component)} onClose={() => setEditingRow(null)} onSaved={() => { setEditingRow(null); doScan(); }} />
      )}
      {previewRow && (
        <PreviewCalcModal api={api} row={previewRow} onClose={() => setPreviewRow(null)} />
      )}
      {bulkOpen && (
        <BulkAssignModal
          api={api}
          rowIds={Array.from(checkedRows)}
          onClose={() => setBulkOpen(false)}
          onSaved={() => { setBulkOpen(false); clearChecks(); doScan(); }}
        />
      )}
    </div>
  );
}


function RowLine({ row, checked, onCheck, onEdit, onPreview, onMarkAudited, onUnmarkAudited, onToggleNeedsRecipe, onDelete }) {
  const audited = !!row.last_audited_at;
  const needsRecipe = !!row.needs_recipe;
  return (
    <tr className={`border-b border-slate-100 hover:bg-slate-50 ${audited ? 'opacity-70' : ''}`} data-testid={`materials-audit-row-${row._id}`}>
      <td className="px-2 py-1.5 text-center">
        <input type="checkbox" checked={checked} onChange={onCheck} data-testid={`materials-audit-row-check-${row._id}`} />
      </td>
      <td className="px-2 py-1.5">
        <div className="font-medium text-slate-800 flex items-center gap-1 flex-wrap">
          {audited && <Check size={10} className="inline text-emerald-600" />}
          {row.name}
          {row.has_recipe && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 font-normal" title={`Recipe · ${row.recipe_entries} entries`}>
              recipe · {row.recipe_entries}
            </span>
          )}
          {row.labour_only && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 font-normal" title="Labour only — no materials required. Materials audit checks are suppressed.">
              labour only
            </span>
          )}
          {row.is_treatment && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-teal-100 text-teal-800 border border-teal-300 font-normal" title="Chemical dilution treatment — buy cost auto-derived from container cost, dilution ratio & default surface.">
              🧪 treatment
            </span>
          )}
          {row.stock_length > 0 && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-300 font-normal" title={`Ships in ${row.stock_length}m sticks / lengths.`}>
              {row.stock_length}m sticks
            </span>
          )}
          {row.quote_required && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-400 font-normal" title="Materials rate is an estimate — confirmed per-job by supplier quote.">
              📋 quote required
            </span>
          )}
          {(row.meta?.underlay?.coverage_m2 || row.meta?.underlay?.type_code) && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300 font-normal" title={`Underlay roll spec (${row.meta?.underlay?.type_name || 'type unknown'})`}>
              {row.meta?.underlay?.coverage_m2 ? `${row.meta.underlay.coverage_m2}m²` : ''}
              {row.meta?.underlay?.type_code ? ` · ${row.meta.underlay.type_code}` : ''}
              {(row.meta?.underlay?.width_mm && row.meta?.underlay?.length_m) ? ` · ${row.meta.underlay.width_mm}×${row.meta.underlay.length_m}` : ''}
            </span>
          )}
          {needsRecipe && !row.has_recipe && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 font-normal inline-flex items-center gap-0.5" title="Operator flagged this row as needing a recipe">
              <ChefHat size={9} /> needs recipe
            </span>
          )}
        </div>
        {row.materials_desc && <div className="text-[10px] text-slate-500 truncate max-w-xs">{row.materials_desc}</div>}
      </td>
      <td className="px-2 py-1.5">{row.supplier || <span className="text-rose-500">—</span>}</td>
      <td className="px-2 py-1.5 font-mono text-[10px]">{row.supplier_code || <span className="text-slate-400">—</span>}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">
        {row.materials_rate > 0 ? `$${row.materials_rate.toFixed(2)}` : <span className="text-rose-500">$0</span>}
      </td>
      <td className="px-2 py-1.5">{row.unit || <span className="text-amber-600">—</span>}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">{row.ratio ?? <span className="text-amber-600">—</span>}</td>
      <td className="px-2 py-1.5">
        <div className="flex flex-wrap gap-0.5">
          {row.issues.map((iss, i) => {
            const meta = SEV_META[iss.severity] || SEV_META.info;
            const Icon = meta.icon;
            return (
              <span key={i} title={iss.hint || iss.label} className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1 py-0.5 rounded border ${meta.klass}`}>
                <Icon size={8} /> {iss.label}
              </span>
            );
          })}
          {row.issues.length === 0 && <span className="text-[10px] text-emerald-600 font-bold">✓ clean</span>}
        </div>
      </td>
      <td className="px-2 py-1.5 text-right whitespace-nowrap">
        <button onClick={onEdit} data-testid={`materials-audit-row-edit-${row._id}`} className="text-[10px] px-1.5 py-0.5 rounded border border-slate-300 bg-white hover:bg-slate-50 mr-1" title="Inline edit">
          Fix
        </button>
        <button onClick={onPreview} data-testid={`materials-audit-row-preview-${row._id}`} className="text-[10px] px-1.5 py-0.5 rounded border border-slate-300 bg-white hover:bg-slate-50 mr-1" title="Calc preview">
          Preview
        </button>
        {row.has_recipe && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 mr-1 inline-flex items-center gap-0.5" title="Recipe attached — edit via Fix">
            <ChefHat size={9} /> recipe · {row.recipe_entries}
          </span>
        )}
        {needsRecipe && !row.has_recipe && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 mr-1 inline-flex items-center gap-0.5 font-semibold" title="Flagged as needing a recipe — click Fix to build">
            <ChefHat size={9} /> build!
          </span>
        )}
        <button
          onClick={onToggleNeedsRecipe}
          data-testid={`materials-audit-row-needs-recipe-${row._id}`}
          className={`text-[10px] px-1.5 py-0.5 rounded border mr-1 ${needsRecipe ? 'border-amber-400 bg-amber-100 text-amber-900 hover:bg-amber-200' : 'border-slate-300 bg-white hover:bg-slate-50 text-slate-600'}`}
          title={needsRecipe ? 'Clear "needs recipe" flag' : 'Flag this row as needing a recipe'}
        >
          {needsRecipe ? '● needs' : '○ needs?'}
        </button>
        <Link
          to={`/component-rates?component=${row.component}&search=${encodeURIComponent(row.name)}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`materials-audit-row-rates-${row._id}`}
          className="text-[10px] px-1.5 py-0.5 rounded border border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100 mr-1 inline-flex items-center gap-0.5"
          title="Open this row in the Component Rates Table"
        >
          <ExternalLink size={9} /> Rates
        </Link>
        {audited ? (
          <button onClick={onUnmarkAudited} data-testid={`materials-audit-row-unmark-${row._id}`} className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 mr-1" title="Unmark audited">
            Unmark
          </button>
        ) : (
          <button onClick={onMarkAudited} data-testid={`materials-audit-row-mark-${row._id}`} className="text-[10px] px-1.5 py-0.5 rounded border border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100 mr-1" title="Mark audited">
            Mark ✓
          </button>
        )}
        <button
          onClick={onDelete}
          data-testid={`materials-audit-row-delete-${row._id}`}
          className="text-[10px] px-1.5 py-0.5 rounded border border-rose-300 bg-white hover:bg-rose-50 text-rose-700 hover:text-rose-800"
          title="Delete this row (cascades to CR mirror + recipe)"
        >
          ×
        </button>
      </td>
    </tr>
  );
}



function StockLengthEditor({ api, rowId, form, setForm }) {
  const active = form.stock_length_charge_mode && form.stock_length_charge_mode !== 'none';
  const [preview, setPreview] = useState(null);
  const [runsInput, setRunsInput] = useState('3.4, 2.9, 5.1');
  const [addLen, setAddLen] = useState('');
  const [addColour, setAddColour] = useState('');
  const debounceRef = useRef(null);

  const runPreview = useCallback(async () => {
    if (!active) { setPreview(null); return; }
    const runs = (runsInput || '')
      .split(/[,\s]+/)
      .map((s) => Number((s || '').trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!runs.length) { setPreview(null); return; }
    try {
      const { data } = await api.post(`/materials-audit/rows/${rowId}/snap-preview`, {
        measured_runs: runs,
        stock_lengths: form.stock_lengths,
      });
      setPreview(data);
    } catch (err) {
      setPreview({ error: err?.response?.data?.detail || err.message });
    }
  }, [api, rowId, runsInput, form.stock_lengths, active]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runPreview, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [runPreview]);

  const addStockLen = () => {
    const n = Number((addLen || '').trim());
    if (!Number.isFinite(n) || n <= 0) return;
    setForm((f) => ({
      ...f,
      stock_lengths: Array.from(new Set([...(f.stock_lengths || []), Math.round(n * 1000) / 1000])).sort((a, b) => a - b),
    }));
    setAddLen('');
  };
  const removeStockLen = (len) => {
    setForm((f) => ({ ...f, stock_lengths: (f.stock_lengths || []).filter((x) => x !== len) }));
  };
  const addColourOpt = () => {
    const c = (addColour || '').trim();
    if (!c) return;
    setForm((f) => {
      const cur = f.colour_options || [];
      if (cur.map((x) => x.toLowerCase()).includes(c.toLowerCase())) return f;
      return { ...f, colour_options: [...cur, c] };
    });
    setAddColour('');
  };
  const removeColourOpt = (c) => {
    setForm((f) => ({ ...f, colour_options: (f.colour_options || []).filter((x) => x !== c) }));
  };

  const bg = active ? 'border-fuchsia-200 bg-fuchsia-50/50' : 'border-slate-200 bg-slate-50/50';
  const labelKlass = active ? 'text-fuchsia-800' : 'text-slate-600';

  return (
    <div className={`col-span-2 border ${bg} rounded-md p-3 space-y-2`} data-testid="materials-audit-edit-stocklengths">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] uppercase tracking-wider ${labelKlass} font-bold`}>📏 Stock length calculator</span>
        <span className="text-[10px] text-slate-500 normal-case tracking-normal">Round measured runs up to the nearest supplier stick (e.g. polycarbonate roofing).</span>
      </div>
      <div className="grid grid-cols-3 gap-3 items-start">
        <label className="col-span-1">
          <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Charge mode</span>
          <select
            value={form.stock_length_charge_mode || 'none'}
            onChange={(e) => setForm((f) => ({ ...f, stock_length_charge_mode: e.target.value }))}
            data-testid="materials-audit-edit-stock_length_charge_mode"
            className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-fuchsia-400 bg-white"
          >
            <option value="none">Off · price per measured lm (no snap)</option>
            <option value="exact">Exact · price × measured lm (no snap)</option>
            <option value="round_up_to_stock">Round up · snap each run to next stock length</option>
          </select>
        </label>
        <label className="col-span-2">
          <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Stock lengths (m)</span>
          <div className="flex flex-wrap gap-1 items-center min-h-[28px] p-1 bg-white border border-slate-300 rounded" data-testid="materials-audit-edit-stock_lengths">
            {(form.stock_lengths || []).map((L) => (
              <button
                key={L}
                type="button"
                onClick={() => removeStockLen(L)}
                data-testid={`materials-audit-edit-stock_length-${L}`}
                title={`Click to remove ${L}m`}
                className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-300 hover:bg-rose-100 hover:text-rose-800 hover:border-rose-300"
              >
                {L}m ×
              </button>
            ))}
            <input
              type="number"
              step="0.1"
              value={addLen}
              onChange={(e) => setAddLen(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addStockLen())}
              placeholder="add…"
              data-testid="materials-audit-edit-stock_length-add"
              className="text-[11px] w-16 border-0 focus:outline-none focus:ring-0 tabular-nums"
            />
          </div>
        </label>
      </div>
      <div className="grid grid-cols-3 gap-3 items-start">
        <label className="col-span-1">
          <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Colour options <span className="normal-case tracking-normal font-normal text-slate-400">for quote line</span></span>
          <div className="flex flex-wrap gap-1 items-center min-h-[28px] p-1 bg-white border border-slate-300 rounded" data-testid="materials-audit-edit-colour_options">
            {(form.colour_options || []).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => removeColourOpt(c)}
                data-testid={`materials-audit-edit-colour-${c.toLowerCase().replace(/\s+/g, '-')}`}
                title={`Click to remove ${c}`}
                className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 border border-indigo-300 hover:bg-rose-100 hover:text-rose-800 hover:border-rose-300"
              >
                {c} ×
              </button>
            ))}
            <input
              type="text"
              value={addColour}
              onChange={(e) => setAddColour(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addColourOpt())}
              placeholder="add…"
              data-testid="materials-audit-edit-colour-add"
              className="text-[11px] w-20 border-0 focus:outline-none focus:ring-0"
            />
          </div>
        </label>
        {active && (
          <label className="col-span-2">
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Preview · measured runs (m, comma-separated)</span>
            <input
              type="text"
              value={runsInput}
              onChange={(e) => setRunsInput(e.target.value)}
              placeholder="3.4, 2.9, 5.1"
              data-testid="materials-audit-edit-stocklength-runs"
              className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-fuchsia-400 tabular-nums"
            />
          </label>
        )}
      </div>
      {active && preview && !preview.error && (
        <div className="mt-1 pt-2 border-t border-fuchsia-200 text-[11px] space-y-1" data-testid="materials-audit-edit-stocklength-preview">
          <div className="grid grid-cols-4 gap-2 text-slate-700">
            <span>Measured total <b className="tabular-nums text-slate-900">{preview.total_measured}m</b></span>
            <span>Billed total <b className="tabular-nums text-fuchsia-800">{preview.total_billed}m</b></span>
            <span>Rate <b className="tabular-nums text-slate-900">${(preview.rate_per_m || 0).toFixed(2)}/m</b></span>
            <span>Line total <b className="tabular-nums text-emerald-700 text-sm">${(preview.line_total || 0).toFixed(2)}</b></span>
          </div>
          <div className="text-slate-600">
            Sticks needed:{' '}
            {(preview.sticks_order || []).length === 0 ? (
              <span className="italic text-slate-400">—</span>
            ) : (
              (preview.sticks_order || []).map((s) => (
                <span key={s.length} className="inline-block bg-white border border-fuchsia-200 rounded px-1.5 py-0.5 mr-1 tabular-nums">
                  {s.qty} × {s.length}m
                </span>
              ))
            )}
          </div>
          <div className="text-[10px] text-slate-500">
            {(preview.runs || []).map((r, i) => (
              <span key={i} className="mr-2">
                {r.measured}m → <b className="text-slate-700">{r.billed}m</b>
                {(r.sticks_used || []).length > 1 && ` (${r.sticks_used.length} sticks)`}
              </span>
            ))}
          </div>
        </div>
      )}
      {active && preview?.error && (
        <div className="text-[11px] text-rose-700">Preview error: {preview.error}</div>
      )}
    </div>
  );
}


function EditRowModal({ api, row, availableComponents = [], onClose, onSaved }) {
  const [form, setForm] = useState({
    name:           row.name || '',
    supplier:       row.supplier || '',
    supplier_code:  row.supplier_code || '',
    materials_rate: row.materials_rate || 0,
    rrp:            row.rrp || 0,
    materials_desc: row.materials_desc || '',
    buy_cost:       row.buy_cost || 0,
    labour_rate:    row.labour_rate || 0,
    contractor_rate: row.contractor_rate || 0,
    pack_size:      row.pack_size || 0,
    pack_unit:      row.pack_unit || '',
    pack_cost:      row.pack_cost || 0,
    labour_only:    !!row.labour_only,
    is_treatment:   !!row.is_treatment,
    quote_required: !!row.quote_required,
    stock_length:   row.stock_length || 0,
    dilution_ratio: row.dilution_ratio || 0,
    container_size_L: row.container_size_L || 0,
    container_cost: row.container_cost || 0,
    surface_rates:  Array.isArray(row.surface_rates) ? row.surface_rates.map(s => ({ ...s })) : [],
    stock_lengths:  Array.isArray(row.stock_lengths) ? [...row.stock_lengths] : [],
    stock_length_charge_mode: row.stock_length_charge_mode || 'none',
    colour_options: Array.isArray(row.colour_options) ? [...row.colour_options] : [],
    cover_width_mm: row.cover_width_mm || 0,
    pricing_units:  Array.isArray(row.pricing_units) ? [...row.pricing_units] : [],
    unit:           row.unit || '',
    ratio:          row.ratio ?? 0,
    meta:           { ...(row.meta || {}) },
  });
  const UNDERLAY_TYPES = {
    SS: 'Self-Support',
    FRSS: 'Fire Resistant, Self-Support',
    HW: 'Heavy Weight',
  };
  const underlayMeta = form.meta?.underlay || {};
  const setUnderlayField = (key, val) => {
    setForm((f) => ({
      ...f,
      meta: {
        ...(f.meta || {}),
        underlay: { ...(f.meta?.underlay || {}), [key]: val,
                    ...(key === 'type_code' ? { type_name: UNDERLAY_TYPES[val] || '' } : {}) },
      },
    }));
  };
  const [saving, setSaving] = useState(false);
  const [schema, setSchema] = useState(null);
  const [unitOptions, setUnitOptions] = useState([]);
  const [showOverrides, setShowOverrides] = useState({}); // { fieldKey: true } for user "Show anyway"
  const [customKey, setCustomKey] = useState('');
  const [supplierList, setSupplierList] = useState([]);
  // Resolve the row's supplier against the letterheads list to detect
  // if it's a pool (multi-provider) and pull member details.
  const supplierLetterhead = supplierList.find(s =>
    (s.display_name || '').toLowerCase() === (form.supplier || '').toLowerCase()
    || (s.aka || []).some(a => a.toLowerCase() === (form.supplier || '').toLowerCase())
  );
  const isPool = !!supplierLetterhead?.is_pool;
  const poolMembers = isPool
    ? (supplierLetterhead.member_slugs || [])
        .map(slug => supplierList.find(s => s.slug === slug))
        .filter(Boolean)
    : [];
  const [supplierEditOpen, setSupplierEditOpen] = useState(false);
  const [supplierEditPrefill, setSupplierEditPrefill] = useState(null);
  // Autosave machinery
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'dirty' | 'saving' | 'saved' | 'error' | 'rename-pending'
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const savedSnapshotRef = useRef(null);
  const savingRef = useRef(false);
  const pendingSaveRef = useRef(false);
  const debounceRef = useRef(null);
  const nameChanged = (form.name || '').trim() !== (row.name || '').trim();
  const hasRecipe = !!row.has_recipe;
  const [pricingExpanded, setPricingExpanded] = useState(!hasRecipe);  // auto-collapse when recipe present
  const [recipeBusy, setRecipeBusy] = useState(false);
  // Pack calculator — count-based fastener pricing without a full recipe.
  // Enabled for fastening rows when the operator has filled all three
  // inputs. When active, buy_cost is derived server-side and locked in UI.
  const showPackCalc = (row.component || '').toLowerCase() === 'fastening' && !hasRecipe;
  // Stock length — descriptive-only field for lineal products (purlins,
  // timber, ridge sticks). Not shown for m² / count-based components.
  const LINEAL_COMPONENTS = new Set(['ridge', 'barge', 'valley', 'flashing', 'spouting', 'other', 'misc']);
  const showStockLength = LINEAL_COMPONENTS.has((row.component || '').toLowerCase());
  const packCalcActive = showPackCalc
    && Number(form.pack_size) > 0
    && Number(form.pack_cost) > 0
    && Number(form.ratio)     > 0;
  const treatmentCalcActive = !!form.is_treatment
    && Number(form.dilution_ratio) > 0
    && Number(form.container_size_L) > 0
    && Number(form.container_cost) > 0
    && (form.surface_rates || []).some(s => s.is_default && Number(s.coverage_L_per_m2) > 0);
  const anyAutoBuyCost = packCalcActive || treatmentCalcActive;
  const packBuyCostPerM2 = packCalcActive
    ? (Number(form.pack_cost) / Number(form.pack_size)) * Number(form.ratio)
    : 0;
  const packCoverage = (Number(form.pack_size) > 0 && Number(form.ratio) > 0)
    ? Number(form.pack_size) / Number(form.ratio)
    : 0;

  const buildRecipe = async () => {
    setRecipeBusy(true);
    try {
      await api.put(`/material-recipes/${row._id}`, {
        component:    row.component,
        name:         form.name || row.name,
        notes:        '',
        entries:      [],
        labour_tasks: [],
      });
      toast.success('Blank recipe created — add entries below');
      onSaved();  // parent re-scans, reopens with has_recipe=true
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setRecipeBusy(false); }
  };

  const simplifyToLumpSum = async () => {
    if (!window.confirm(
      `Delete the recipe for "${row.name}" and revert to a lump-sum row?\n\n` +
      `All recipe entries (products, ratios, coverage, labour tasks) will be permanently removed. The parent supplier/code/unit/rate fields will become editable again. This action cannot be undone.`,
    )) return;
    setRecipeBusy(true);
    try {
      await api.delete(`/material-recipes/${row._id}`);
      toast.success('Recipe removed — parent fields now editable');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setRecipeBusy(false); }
  };

  const reloadSuppliers = useCallback(async () => {
    try {
      const { data } = await api.get('/supplier-letterheads');
      setSupplierList(data?.letterheads || data || []);
    } catch { /* non-fatal */ }
  }, [api]);

  useEffect(() => {
    (async () => {
      try {
        const { data: sch } = await api.get('/materials-audit/component-meta-schema');
        setSchema(sch?.schema || {});
        setUnitOptions(sch?.unit_options || []);
      } catch { /* non-fatal */ }
      reloadSuppliers();
    })();
  }, [api, reloadSuppliers]);

  // Support both new schema shape {fields, hide_standard} and legacy list.
  const compSchema = (schema && schema[row.component]) || null;
  const schemaFields = Array.isArray(compSchema) ? compSchema : (compSchema?.fields || []);
  const hideStandard = new Set(Array.isArray(compSchema) ? [] : (compSchema?.hide_standard || []));

  // Match supplier ↔ letterhead by display_name OR slug OR aka.
  const currentSupplierEntry = supplierList.find((s) =>
    s.display_name === form.supplier
    || s.slug === (form.supplier || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    || (s.aka || []).includes(form.supplier)
  );
  const supplierExists = !!currentSupplierEntry;

  const setMeta = (k, v) => setForm((f) => ({ ...f, meta: { ...f.meta, [k]: v } }));
  const addCustomField = () => {
    const k = customKey.trim();
    if (!k) return;
    setMeta(k, '');
    setCustomKey('');
  };

  const onSupplierChange = (value) => {
    if (value === '__add_new__') {
      // Open the nested "Add supplier" modal, pre-filled with the
      // currently-typed supplier name (if any) so the user can just
      // finish the details and save.
      setSupplierEditPrefill({
        slug: (form.supplier || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
        display_name: form.supplier || '',
        aka: [], accent_color: '#1F2937',
      });
      setSupplierEditOpen(true);
      return;
    }
    setForm((f) => ({ ...f, supplier: value }));
  };

  const onSupplierCreated = (payload) => {
    // Payload comes back from the child modal's onSaved. Persist to
    // backend then set the supplier field to the new display_name.
    (async () => {
      try {
        await api.put(`/supplier-letterheads/${payload.slug}`, payload);
        toast.success(`Added supplier "${payload.display_name}"`);
        setForm((f) => ({ ...f, supplier: payload.display_name }));
        setSupplierEditOpen(false);
        reloadSuppliers();
      } catch (err) {
        toast.error(err?.response?.data?.detail || err.message);
      }
    })();
  };

  // Persist the current form. `opts.confirmRename` gates the cascade prompt.
  const persist = useCallback(async (formSnapshot, opts = {}) => {
    if (!(formSnapshot.name || '').trim()) {
      toast.error('Name cannot be blank');
      throw new Error('name-blank');
    }
    savingRef.current = true;
    setSaveStatus('saving');
    try {
      const { data } = await api.patch(`/materials-audit/rows/${row._id}`, formSnapshot);
      savedSnapshotRef.current = JSON.stringify(formSnapshot);
      setLastSavedAt(new Date());
      setSaveStatus('saved');
      if (data?.rename_report && (formSnapshot.name || '').trim() !== (row.name || '').trim()) {
        const rr = data.rename_report;
        toast.success(
          `Renamed everywhere · quote_items ${rr.quote_items_updated} · component_lines ${rr.component_lines_updated} · recipes ${rr.material_recipes_updated}`,
        );
      } else if (opts.silent !== true) {
        toast.success('Row updated');
      }
      return data;
    } catch (err) {
      setSaveStatus('error');
      toast.error(err?.response?.data?.detail || err.message);
      throw err;
    } finally {
      savingRef.current = false;
    }
  }, [api, row._id, row.name]);

  // Debounced autosave — fires 800ms after last field edit.
  // Skips when name has changed (cascade rename requires explicit action).
  useEffect(() => {
    // Initialise snapshot on first render.
    if (savedSnapshotRef.current === null) {
      savedSnapshotRef.current = JSON.stringify(form);
      return;
    }
    const currentJson = JSON.stringify(form);
    if (currentJson === savedSnapshotRef.current) {
      // Form matches last-saved state — clear any stale rename-pending/dirty status.
      setSaveStatus((s) => (s === 'saving' || s === 'error') ? s : (lastSavedAt ? 'saved' : 'idle'));
      return;
    }
    if (nameChanged) {
      // Renamed → wait for explicit Save & Close (with confirm prompt).
      setSaveStatus('rename-pending');
      return;
    }
    setSaveStatus('dirty');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (savingRef.current) {
        // Another save in-flight — mark that we need another pass.
        pendingSaveRef.current = true;
        return;
      }
      persist(form, { silent: true }).catch(() => {}).then(() => {
        // Coalesce any edits that landed while we were saving.
        if (pendingSaveRef.current) {
          pendingSaveRef.current = false;
          setSaveStatus('dirty');
        }
      });
    }, 800);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [form, nameChanged, persist, lastSavedAt]);

  // Final commit — flushes any pending debounce, then closes.
  const finalSaveAndClose = async () => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    if (nameChanged) {
      const ok = window.confirm(
        `Rename "${row.name}" → "${form.name.trim()}"?\n\n` +
        `This will cascade across every appraisal that references the old name (quote_items, component_lines, material_recipes, catalogue mirror). Cannot be undone in one click.`,
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      // If no local changes, still call onSaved so parent re-scans.
      if (JSON.stringify(form) !== savedSnapshotRef.current) {
        await persist(form, { silent: nameChanged /* don't double-toast */ });
      }
      onSaved();
    } catch { /* toast already shown */ }
    finally { setSaving(false); }
  };

  // If the modal is closed while a debounced save is still pending
  // (or a rename hasn't been committed), warn the operator so they
  // don't lose the name-change intent. Autosaved value changes are safe.
  const handleClose = () => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    // If we have unflushed pending autosaves, fire and forget (best-effort).
    const currentJson = JSON.stringify(form);
    if (!nameChanged && currentJson !== savedSnapshotRef.current) {
      persist(form, { silent: true }).catch(() => {});
    }
    if (nameChanged) {
      const ok = window.confirm(
        `You changed the row name but haven't committed the rename cascade yet.\n\nClose and DISCARD the name change?`,
      );
      if (!ok) return;
    }
    // Notify parent so its scan/list reflects any autosaved fields.
    if (savedSnapshotRef.current && savedSnapshotRef.current !== JSON.stringify({
      name: row.name || '', supplier: row.supplier || '', supplier_code: row.supplier_code || '',
      materials_rate: row.materials_rate || 0, rrp: row.rrp || 0,
      materials_desc: row.materials_desc || '', buy_cost: row.buy_cost || 0,
      labour_rate: row.labour_rate || 0, contractor_rate: row.contractor_rate || 0,
      pack_size: row.pack_size || 0, pack_unit: row.pack_unit || '', pack_cost: row.pack_cost || 0,
      labour_only: !!row.labour_only,
      is_treatment: !!row.is_treatment,
      quote_required: !!row.quote_required,
      stock_length: row.stock_length || 0,
      dilution_ratio: row.dilution_ratio || 0,
      container_size_L: row.container_size_L || 0,
      container_cost: row.container_cost || 0,
      surface_rates: Array.isArray(row.surface_rates) ? row.surface_rates.map(s => ({ ...s })) : [],
      stock_lengths: Array.isArray(row.stock_lengths) ? [...row.stock_lengths] : [],
      stock_length_charge_mode: row.stock_length_charge_mode || 'none',
      colour_options: Array.isArray(row.colour_options) ? [...row.colour_options] : [],
      cover_width_mm: row.cover_width_mm || 0,
      pricing_units:  Array.isArray(row.pricing_units) ? [...row.pricing_units] : [],
      unit: row.unit || '', ratio: row.ratio ?? 0, meta: { ...(row.meta || {}) },
    })) {
      onSaved();
    } else {
      onClose();
    }
  };

  // Legacy name kept so existing button click handlers don't break; delegates.
  const save = finalSaveAndClose;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={handleClose} data-testid="materials-audit-edit-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl my-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">Fix row — {row.name}</h3>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, quote_required: !f.quote_required }))}
              data-testid="materials-audit-edit-quote_required-toggle"
              title={form.quote_required
                ? 'Quote required — materials_rate is treated as an estimate; missing_supplier_code check is suppressed. Click to disable.'
                : 'Flag this row as needing a per-job supplier quote (e.g. scaffolding). Rate becomes an estimate; supplier_code check is suppressed.'}
              className={`text-[11px] px-2.5 py-1 rounded-md border font-semibold inline-flex items-center gap-1 transition-colors ${form.quote_required ? 'bg-amber-600 text-white border-amber-600 hover:bg-amber-700' : 'bg-white text-slate-600 border-slate-300 hover:bg-amber-50 hover:border-amber-300'}`}
            >
              {form.quote_required ? '☑' : '☐'} 📋 Quote required
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, labour_only: !f.labour_only }))}
              data-testid="materials-audit-edit-labour_only-toggle"
              title={form.labour_only
                ? 'This row is flagged Labour only — materials checks are suppressed. Click to reinstate materials.'
                : 'Flag this row as labour-only (e.g. house wash) — hides materials fields and skips all supplier/rate audit checks.'}
              className={`text-[11px] px-2.5 py-1 rounded-md border font-semibold inline-flex items-center gap-1 transition-colors ${form.labour_only ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-amber-50 hover:border-amber-300'}`}
            >
              {form.labour_only ? '☑' : '☐'} Labour only
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({
                ...f,
                is_treatment: !f.is_treatment,
                // Seed sensible defaults on first activation so the operator
                // sees a working preview immediately.
                dilution_ratio:  f.dilution_ratio  || 30,
                container_size_L: f.container_size_L || 20,
                container_cost:   f.container_cost   || 280,
                surface_rates: (f.surface_rates && f.surface_rates.length) ? f.surface_rates : [
                  { surface: 'Concrete driveway', coverage_L_per_m2: 2.5, is_default: true  },
                  { surface: 'Concrete roof',     coverage_L_per_m2: 2.0, is_default: false },
                  { surface: 'Iron roof',         coverage_L_per_m2: 1.0, is_default: false },
                ],
              }))}
              data-testid="materials-audit-edit-is_treatment-toggle"
              title={form.is_treatment
                ? 'Treatment (chemical dilution) is ON. Buy cost is auto-derived from the default surface. Click to disable.'
                : 'Flag this row as a chemical dilution treatment — opens the calculator (container cost, dilution ratio, surface coverage rates).'}
              className={`text-[11px] px-2.5 py-1 rounded-md border font-semibold inline-flex items-center gap-1 transition-colors ${form.is_treatment ? 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700' : 'bg-white text-slate-600 border-slate-300 hover:bg-teal-50 hover:border-teal-300'}`}
            >
              {form.is_treatment ? '☑' : '☐'} 🧪 Treatment
            </button>
            <AutosaveIndicator status={saveStatus} lastSavedAt={lastSavedAt} />
            <button onClick={handleClose} className="text-slate-400 hover:text-slate-700" data-testid="materials-audit-edit-close-x"><X size={16} /></button>
          </div>
        </div>

        <div className="p-4 overflow-y-auto space-y-3">
          {/* Name */}
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">
              Row name
              {nameChanged && (
                <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 normal-case tracking-normal font-normal">
                  ⚠ Cascades to all appraisals + recipes
                </span>
              )}
            </span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="materials-audit-edit-name"
              className={`mt-0.5 w-full text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400 ${nameChanged ? 'border-amber-400 bg-amber-50' : 'border-slate-300'}`}
            />
          </label>

          {/* Quote-required banner — rate is a placeholder; actual price
              comes from a per-job supplier quote (e.g. scaffolders). */}
          {form.quote_required && (
            <div className="border border-amber-400 bg-amber-50 rounded-md px-3 py-2 text-[11px] text-amber-900" data-testid="materials-audit-edit-quote_required-banner">
              <b>📋 Quote required</b> — the Materials rate below is an <b>estimated placeholder</b>. Actual pricing must be confirmed per-job by a supplier quote (e.g. {form.supplier || 'the assigned scaffolder'}). SKU / supplier_code varies per job and is not required on this row.
            </div>
          )}

          {/* Labour-only banner + labour-side rates when the row has no
              materials (e.g. Gutter Clean / House Wash / Roof Wash).
              Suppresses the whole materials pricing + pack + recipe
              section further down. */}
          {form.labour_only && (
            <div className="border border-amber-300 bg-amber-50 rounded-md p-3 space-y-2" data-testid="materials-audit-edit-labour_only-section">
              <div className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                🧽 Labour only — no materials required
                <span className="text-[10px] font-normal text-amber-700 normal-case">All materials audit checks (supplier · code · unit · rate · buy cost · ratio) are suppressed for this row.</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <label>
                  <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Labour rate ($ / unit)</span>
                  <input type="number" step="0.01" value={form.labour_rate} onChange={(e) => setForm((f) => ({ ...f, labour_rate: Number(e.target.value) }))} data-testid="materials-audit-edit-labour_only-labour_rate" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-400" />
                </label>
                <label>
                  <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Contractor rate ($ / unit)</span>
                  <input type="number" step="0.01" value={form.contractor_rate} onChange={(e) => setForm((f) => ({ ...f, contractor_rate: Number(e.target.value) }))} data-testid="materials-audit-edit-labour_only-contractor_rate" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-400" />
                </label>
              </div>
            </div>
          )}

          <div className={form.labour_only ? 'hidden' : ''}>

          {/* Pricing — collapsed to a read-only summary when a recipe is present. */}
          {hasRecipe && !pricingExpanded ? (
            <div className="border border-emerald-200 bg-emerald-50/40 rounded-md px-3 py-2 text-xs flex items-center gap-3" data-testid="materials-audit-edit-pricing-summary">
              <ChefHat size={14} className="text-emerald-600" />
              <div className="flex-1">
                <div className="font-semibold text-emerald-900">
                  Supplied by recipe · {row.recipe_entries} entr{row.recipe_entries === 1 ? 'y' : 'ies'}
                </div>
                <div className="text-[10px] text-slate-600 mt-0.5">
                  Supplier · code · unit · ratio · buy_cost are driven by the recipe entries below. Peek at raw parent values by expanding.
                </div>
              </div>
              {(row.issues || []).some((i) => i.code === 'recipe_buy_cost_drift_gt_2pct') && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const { data } = await api.post(`/materials-audit/rows/${row._id}/sync-buy-cost-from-recipe`);
                      toast.success(`Buy cost synced: $${data.old_buy_cost.toFixed(4)} → $${data.new_buy_cost.toFixed(4)}`);
                      onSaved && onSaved();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Sync failed');
                    }
                  }}
                  data-testid="materials-audit-edit-sync-buy-cost"
                  title="Overwrite the CRT parent's buy_cost with the recipe rollup — clears the drift audit warning."
                  className="text-[10px] px-2 py-1 rounded bg-orange-500 text-white hover:bg-orange-600 font-semibold inline-flex items-center gap-1"
                >
                  🔄 Sync buy_cost to recipe
                </button>
              )}
              <button
                type="button"
                onClick={() => setPricingExpanded(true)}
                data-testid="materials-audit-edit-pricing-expand"
                className="text-[10px] px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
              >Peek at parent values ▾</button>
            </div>
          ) : (
            <div>
              {hasRecipe && (
                <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2 flex items-center justify-between">
                  <span>⚠ These fields are ignored when a recipe exists — edit the recipe entries below instead. Shown here for reference.</span>
                  <button
                    type="button"
                    onClick={() => setPricingExpanded(false)}
                    data-testid="materials-audit-edit-pricing-collapse"
                    className="text-[10px] px-1.5 py-0.5 rounded border border-amber-300 bg-white hover:bg-amber-100 text-amber-800"
                  >Collapse ▴</button>
                </div>
              )}
              {/* Standard fields grid */}
              <div className="grid grid-cols-2 gap-3 text-xs">
            <label>
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 flex items-center justify-between">
                <span>Supplier</span>
                {form.supplier && supplierExists && currentSupplierEntry && (
                  <Link
                    to={`/admin/suppliers?slug=${currentSupplierEntry.slug}`}
                    target="_blank"
                    data-testid="materials-audit-edit-supplier-link"
                    className="text-[9px] normal-case tracking-normal font-normal text-sky-600 hover:text-sky-800 underline"
                  >
                    Edit details →
                  </Link>
                )}
              </span>
              <select
                value={form.supplier || ''}
                onChange={(e) => onSupplierChange(e.target.value)}
                data-testid="materials-audit-edit-supplier"
                className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400 bg-white"
              >
                <option value="">— No supplier —</option>
                {/* Include the current value even if it doesn't match a letterhead */}
                {form.supplier && !supplierExists && (
                  <option value={form.supplier}>{form.supplier} (no letterhead — orphan)</option>
                )}
                {supplierList.map((s) => (
                  <option key={s.slug} value={s.display_name}>{s.display_name}</option>
                ))}
                <option value="__add_new__" data-testid="materials-audit-edit-supplier-add-new">➕ Add new supplier…</option>
              </select>
            </label>
            <label>
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Supplier code</span>
              <input type="text" value={form.supplier_code} onChange={(e) => setForm((f) => ({ ...f, supplier_code: e.target.value }))} data-testid="materials-audit-edit-supplier_code" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400" />
            </label>
            <label>
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Buy cost ($)</span>
              <input
                type="number"
                step="0.01"
                value={form.buy_cost}
                onChange={(e) => setForm((f) => ({ ...f, buy_cost: Number(e.target.value) }))}
                data-testid="materials-audit-edit-buy_cost"
                disabled={anyAutoBuyCost}
                title={anyAutoBuyCost ? 'Auto-computed from the calculator below — clear pack / treatment fields to edit manually' : ''}
                className={`w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400 ${anyAutoBuyCost ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`}
              />
            </label>
            <label>
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Materials rate ($)</span>
              <input type="number" step="0.01" value={form.materials_rate} onChange={(e) => setForm((f) => ({ ...f, materials_rate: Number(e.target.value) }))} data-testid="materials-audit-edit-materials_rate" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400" />
            </label>
            <label>
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Labour rate ($ / unit)</span>
              <input type="number" step="0.01" value={form.labour_rate} onChange={(e) => setForm((f) => ({ ...f, labour_rate: Number(e.target.value) }))} data-testid="materials-audit-edit-labour_rate" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400" />
            </label>
            <label>
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Contractor rate ($ / unit)</span>
              <input type="number" step="0.01" value={form.contractor_rate} onChange={(e) => setForm((f) => ({ ...f, contractor_rate: Number(e.target.value) }))} data-testid="materials-audit-edit-contractor_rate" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400" />
            </label>
            {showStockLength && (
              <label>
                <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Stock length (m)</span>
                <input
                  type="number"
                  step="0.1"
                  value={form.stock_length}
                  onChange={(e) => setForm((f) => ({ ...f, stock_length: Number(e.target.value) }))}
                  placeholder="6.0"
                  data-testid="materials-audit-edit-stock_length"
                  title="Length per stick / bar / length as supplied (metres). Used to compute stick counts for orders."
                  className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400"
                />
              </label>
            )}
            <label>
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">RRP · Recommended Retail ($) <span className="normal-case tracking-normal font-normal text-slate-400">reference only</span></span>
              <input type="number" step="0.01" value={form.rrp} onChange={(e) => setForm((f) => ({ ...f, rrp: Number(e.target.value) }))} data-testid="materials-audit-edit-rrp" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400" />
            </label>
            {(!hideStandard.has('unit') || showOverrides.unit) ? (
              <label>
                <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 flex items-center justify-between">
                  <span>Unit</span>
                  {hideStandard.has('unit') && (
                    <button
                      type="button"
                      onClick={() => setShowOverrides((o) => ({ ...o, unit: false }))}
                      className="text-[9px] normal-case tracking-normal font-normal text-slate-400 hover:text-rose-600"
                      data-testid="materials-audit-edit-unit-hide"
                    >× not required</button>
                  )}
                </span>
                <select
                  value={form.unit || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '__custom__') {
                      const custom = window.prompt('Custom unit:', form.unit || '');
                      if (custom !== null) setForm((f) => ({ ...f, unit: custom.trim() }));
                    } else {
                      setForm((f) => ({ ...f, unit: v }));
                    }
                  }}
                  data-testid="materials-audit-edit-unit"
                  className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400 bg-white"
                >
                  <option value="">— No unit —</option>
                  {form.unit && !unitOptions.includes(form.unit) && (
                    <option value={form.unit}>{form.unit} (custom)</option>
                  )}
                  {unitOptions.map((u) => <option key={u} value={u}>{u}</option>)}
                  <option value="__custom__" data-testid="materials-audit-edit-unit-custom">➕ Custom unit…</option>
                </select>
              </label>
            ) : (
              <NotRequiredChip
                label="Unit"
                onEnable={() => setShowOverrides((o) => ({ ...o, unit: true }))}
                testId="materials-audit-edit-unit-notreq"
              />
            )}
            {(!hideStandard.has('ratio') || showOverrides.ratio) ? (
              <label>
                <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 flex items-center justify-between">
                  <span>Ratio</span>
                  {hideStandard.has('ratio') && (
                    <button
                      type="button"
                      onClick={() => setShowOverrides((o) => ({ ...o, ratio: false }))}
                      className="text-[9px] normal-case tracking-normal font-normal text-slate-400 hover:text-rose-600"
                      data-testid="materials-audit-edit-ratio-hide"
                    >× not required</button>
                  )}
                </span>
                <input type="number" step="0.01" value={form.ratio} onChange={(e) => setForm((f) => ({ ...f, ratio: Number(e.target.value) }))} data-testid="materials-audit-edit-ratio" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400" />
              </label>
            ) : (
              <NotRequiredChip
                label="Ratio"
                onEnable={() => setShowOverrides((o) => ({ ...o, ratio: true }))}
                testId="materials-audit-edit-ratio-notreq"
              />
            )}
            <label className="col-span-2">
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Materials desc</span>
              <input type="text" value={form.materials_desc} onChange={(e) => setForm((f) => ({ ...f, materials_desc: e.target.value }))} data-testid="materials-audit-edit-materials_desc" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400" />
            </label>
            {isPool && (
              <div className="col-span-2 border border-indigo-300 bg-indigo-50/50 rounded-md p-3 space-y-2" data-testid="materials-audit-edit-pool-members">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-indigo-800 font-bold">🏗️ Supplier pool · {poolMembers.length} {poolMembers.length === 1 ? 'member' : 'members'}</span>
                  <span className="text-[10px] text-slate-500 normal-case tracking-normal">Any pool member can be selected at quote time. Click 📞 to call or ✉ to email.</span>
                </div>
                {poolMembers.length === 0 ? (
                  <div className="text-[10px] text-slate-400 italic py-1">No members yet — attach members via the Suppliers Admin page.</div>
                ) : (
                  <ul className="divide-y divide-indigo-100">
                    {poolMembers.map((m) => {
                      // Extract phone/email from notes when the letterhead
                      // doesn't have dedicated fields yet.
                      const phoneMatch = /(\d[\d\s-]{6,})/.exec(m.phone || m.notes || '');
                      const emailMatch = /([\w.+-]+@[\w-]+\.[\w.-]+)/i.exec(m.email || m.notes || '');
                      return (
                        <li key={m.slug} className="py-1.5 flex items-center gap-3 text-[11px]">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: m.accent_color || '#6366F1' }} />
                          <span className="font-semibold text-slate-800 min-w-0 flex-1 truncate">{m.display_name}</span>
                          {phoneMatch && (
                            <a href={`tel:${phoneMatch[1].replace(/\s/g, '')}`} className="text-slate-600 hover:text-indigo-700" title={phoneMatch[1]}>📞 {phoneMatch[1]}</a>
                          )}
                          {emailMatch && (
                            <a href={`mailto:${emailMatch[1]}`} className="text-slate-600 hover:text-indigo-700 truncate" title={emailMatch[1]}>✉ {emailMatch[1]}</a>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
            {(row.component || '').toLowerCase() === 'underlay' && (
              <div className="col-span-2 border border-blue-200 bg-blue-50/40 rounded-md p-3 space-y-2" data-testid="materials-audit-edit-underlay">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-blue-800 font-bold">🧻 Underlay specs</span>
                  <span className="text-[10px] text-slate-500 normal-case tracking-normal">Roll coverage · type · width · length. Auto-fill parses these from the row name / description.</span>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const { data } = await api.post('/materials-audit/infer-meta', { component: 'underlay', dry_run: true, row_ids: [row._id] });
                        const proposal = (data.proposals || [])[0];
                        const patch = proposal?.patch?.meta?.underlay;
                        if (!patch || !Object.keys(patch).length) {
                          toast.info('No new specs could be inferred from the name / description.');
                          return;
                        }
                        setForm((f) => ({ ...f, meta: { ...(f.meta || {}), underlay: { ...(f.meta?.underlay || {}), ...patch } } }));
                        toast.success(`Auto-filled: ${Object.keys(patch).join(', ')}`);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Auto-fill failed');
                      }
                    }}
                    data-testid="materials-audit-edit-underlay-autofill"
                    className="ml-auto text-[10px] px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700 font-semibold"
                  >🔍 Auto-fill from description</button>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <label>
                    <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Coverage (m² / roll)</span>
                    <input type="number" step="0.1" value={underlayMeta.coverage_m2 || ''} onChange={(e) => setUnderlayField('coverage_m2', Number(e.target.value) || 0)} placeholder="50" data-testid="materials-audit-edit-underlay-coverage" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                  </label>
                  <label>
                    <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Type code</span>
                    <select value={underlayMeta.type_code || ''} onChange={(e) => setUnderlayField('type_code', e.target.value)} data-testid="materials-audit-edit-underlay-type_code" className="w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400">
                      <option value="">—</option>
                      {Object.entries(UNDERLAY_TYPES).map(([code, name]) => (
                        <option key={code} value={code}>{code} · {name}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Roll width (mm)</span>
                    <input type="number" step="1" value={underlayMeta.width_mm || ''} onChange={(e) => setUnderlayField('width_mm', Number(e.target.value) || 0)} placeholder="1350" data-testid="materials-audit-edit-underlay-width" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                  </label>
                  <label>
                    <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Roll length (m)</span>
                    <input type="number" step="0.1" value={underlayMeta.length_m || ''} onChange={(e) => setUnderlayField('length_m', Number(e.target.value) || 0)} placeholder="37" data-testid="materials-audit-edit-underlay-length" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                  </label>
                </div>
              </div>
            )}
            {form.is_treatment && (() => {
              const dilution = Number(form.dilution_ratio) || 0;
              const containerL = Number(form.container_size_L) || 0;
              const containerCost = Number(form.container_cost) || 0;
              const concPerL = (containerL > 0) ? (containerCost / containerL) : 0;
              const denom = dilution + 1;
              const defaultSurface = (form.surface_rates || []).find(s => s.is_default);
              const derivedBuyCost = (defaultSurface && denom > 0 && defaultSurface.coverage_L_per_m2 > 0)
                ? (Number(defaultSurface.coverage_L_per_m2) / denom) * concPerL
                : 0;
              const updateRate = (idx, key, value) => setForm((f) => ({
                ...f,
                surface_rates: f.surface_rates.map((r, i) => {
                  if (i !== idx) return { ...r, is_default: key === 'is_default' ? false : r.is_default };
                  return { ...r, [key]: key === 'is_default' ? true : value };
                }),
              }));
              const removeRate = (idx) => setForm((f) => {
                const next = f.surface_rates.filter((_, i) => i !== idx);
                if (!next.some(r => r.is_default) && next.length) next[0].is_default = true;
                return { ...f, surface_rates: next };
              });
              const addRate = () => setForm((f) => ({
                ...f,
                surface_rates: [...(f.surface_rates || []), { surface: '', coverage_L_per_m2: 0, is_default: (f.surface_rates || []).length === 0 }],
              }));
              return (
                <div className="col-span-2 border border-teal-300 bg-teal-50/40 rounded-md p-3 space-y-3" data-testid="materials-audit-edit-treatment">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-teal-800 font-bold">🧪 Treatment calculator</span>
                    <span className="text-[10px] text-slate-500 normal-case tracking-normal">Concentrate is mixed with water; the mixed solution is applied per m² of surface. Default surface drives the row&apos;s buy cost.</span>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <label>
                      <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Container size (L)</span>
                      <input type="number" step="0.1" value={form.container_size_L} onChange={(e) => setForm((f) => ({ ...f, container_size_L: Number(e.target.value) }))} data-testid="materials-audit-edit-container_size_L" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-400" />
                    </label>
                    <label>
                      <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Container cost ($)</span>
                      <input type="number" step="0.01" value={form.container_cost} onChange={(e) => setForm((f) => ({ ...f, container_cost: Number(e.target.value) }))} data-testid="materials-audit-edit-container_cost" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-400" />
                    </label>
                    <label>
                      <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Dilution ratio (water : conc)</span>
                      <input type="number" step="1" value={form.dilution_ratio} onChange={(e) => setForm((f) => ({ ...f, dilution_ratio: Number(e.target.value) }))} data-testid="materials-audit-edit-dilution_ratio" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-400" />
                    </label>
                    <div>
                      <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Concentrate $ / L</span>
                      <div className="text-sm font-semibold text-teal-800 tabular-nums px-2 py-1" data-testid="materials-audit-edit-conc-per-L">
                        {concPerL > 0 ? `$${concPerL.toFixed(2)}` : '—'}
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-teal-200 pt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] uppercase tracking-wider text-teal-800 font-semibold">Surface coverage rates</span>
                      <button type="button" onClick={addRate} data-testid="materials-audit-edit-treatment-add-surface" className="text-[10px] px-2 py-0.5 rounded bg-teal-600 text-white hover:bg-teal-700 font-semibold">+ Add surface</button>
                    </div>
                    <table className="w-full text-[11px]">
                      <thead className="text-[10px] uppercase text-slate-500">
                        <tr>
                          <th className="text-left pb-1 w-8">Default</th>
                          <th className="text-left pb-1">Surface</th>
                          <th className="text-right pb-1">Mixed L / m²</th>
                          <th className="text-right pb-1">Concentrate L / m²</th>
                          <th className="text-right pb-1">Cost / m²</th>
                          <th className="pb-1 w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(form.surface_rates || []).map((r, idx) => {
                          const cov = Number(r.coverage_L_per_m2) || 0;
                          const conc_pm2 = denom > 0 ? cov / denom : 0;
                          const cost_pm2 = conc_pm2 * concPerL;
                          return (
                            <tr key={idx} className={r.is_default ? 'bg-teal-100/60' : ''}>
                              <td className="py-1">
                                <input type="radio" name="treatment-default-surface" checked={!!r.is_default} onChange={() => updateRate(idx, 'is_default', true)} data-testid={`materials-audit-edit-treatment-default-${idx}`} className="accent-teal-600" />
                              </td>
                              <td className="py-1 pr-2">
                                <input type="text" value={r.surface || ''} onChange={(e) => updateRate(idx, 'surface', e.target.value)} placeholder="e.g. Concrete driveway" data-testid={`materials-audit-edit-treatment-surface-${idx}`} className="w-full text-[11px] border border-slate-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-teal-400" />
                              </td>
                              <td className="py-1 pr-2 text-right">
                                <input type="number" step="0.1" value={r.coverage_L_per_m2 ?? 0} onChange={(e) => updateRate(idx, 'coverage_L_per_m2', Number(e.target.value))} data-testid={`materials-audit-edit-treatment-coverage-${idx}`} className="w-20 text-[11px] border border-slate-300 rounded px-1.5 py-0.5 text-right focus:outline-none focus:ring-1 focus:ring-teal-400" />
                              </td>
                              <td className="py-1 text-right tabular-nums text-slate-600">{conc_pm2 > 0 ? conc_pm2.toFixed(4) : '—'}</td>
                              <td className="py-1 text-right tabular-nums font-semibold text-emerald-700">{cost_pm2 > 0 ? `$${cost_pm2.toFixed(4)}` : '—'}</td>
                              <td className="py-1 text-right">
                                <button type="button" onClick={() => removeRate(idx)} className="text-slate-400 hover:text-rose-500 text-xs" title="Remove surface" data-testid={`materials-audit-edit-treatment-remove-${idx}`}>×</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {(!form.surface_rates || form.surface_rates.length === 0) && (
                      <div className="text-[10px] text-slate-400 italic py-2">No surface rates yet — click &quot;+ Add surface&quot; to define one.</div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-600 pt-1 border-t border-teal-200">
                    <span>
                      Default surface:{' '}
                      <b className="text-teal-800">{defaultSurface?.surface || '—'}</b>
                    </span>
                    <span title="concentrate_L/m² × concentrate_$/L">
                      Buy cost /m²{' '}
                      <b className="text-emerald-700 tabular-nums text-sm" data-testid="materials-audit-edit-treatment-derived-buycost">
                        {derivedBuyCost > 0 ? `$${derivedBuyCost.toFixed(4)}` : '—'}
                      </b>
                    </span>
                    {derivedBuyCost > 0 && (
                      <span className="ml-auto text-[10px] text-emerald-700 font-semibold">
                        ✓ Buy cost auto-derived from default surface — overrides the manual field above
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}
            {showPackCalc && (
              <div className="col-span-2 border border-sky-200 bg-sky-50/50 rounded-md p-3 space-y-2" data-testid="materials-audit-edit-packcalc">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-sky-800 font-bold">📦 Pack calculator</span>
                  <span className="text-[10px] text-slate-500 normal-case tracking-normal">Fill pack size + cost + ratio to auto-derive buy cost /m² without building a recipe</span>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <label>
                    <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Pack size (units)</span>
                    <input type="number" step="0.01" value={form.pack_size} onChange={(e) => setForm((f) => ({ ...f, pack_size: Number(e.target.value) }))} placeholder="100" data-testid="materials-audit-edit-pack_size" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-sky-400" />
                  </label>
                  <label>
                    <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Pack unit</span>
                    <input type="text" list="pack-unit-vocab" value={form.pack_unit} onChange={(e) => setForm((f) => ({ ...f, pack_unit: e.target.value }))} placeholder="box" data-testid="materials-audit-edit-pack_unit" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-sky-400" />
                    <datalist id="pack-unit-vocab">
                      <option value="box" /><option value="packet" /><option value="bag" /><option value="each" />
                      <option value="sleeve" /><option value="tub" /><option value="drum" /><option value="roll" />
                    </datalist>
                  </label>
                  <label>
                    <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Pack cost ($)</span>
                    <input type="number" step="0.01" value={form.pack_cost} onChange={(e) => setForm((f) => ({ ...f, pack_cost: Number(e.target.value) }))} placeholder="9.85" data-testid="materials-audit-edit-pack_cost" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-sky-400" />
                  </label>
                  <label>
                    <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Ratio (units / m²)</span>
                    <input type="number" step="0.01" value={form.ratio} onChange={(e) => setForm((f) => ({ ...f, ratio: Number(e.target.value) }))} placeholder="7" data-testid="materials-audit-edit-packcalc-ratio" className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-sky-400" />
                  </label>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-600 pt-1 border-t border-sky-200">
                  <span>
                    Pack covers{' '}
                    <b className="text-slate-800 tabular-nums" data-testid="materials-audit-edit-packcalc-coverage">
                      {packCoverage > 0 ? `${packCoverage.toFixed(2)} m²` : '—'}
                    </b>
                  </span>
                  <span title="pack_cost ÷ pack_size × ratio">
                    Buy cost /m²{' '}
                    <b className="text-emerald-700 tabular-nums text-sm" data-testid="materials-audit-edit-packcalc-buycost">
                      {packBuyCostPerM2 > 0 ? `$${packBuyCostPerM2.toFixed(4)}` : '—'}
                    </b>
                  </span>
                  {packCalcActive && (
                    <span className="ml-auto text-[10px] text-emerald-700 font-semibold">
                      ✓ Buy cost auto-derived — overrides the manual field above
                    </span>
                  )}
                </div>
              </div>
            )}
            <StockLengthEditor
              api={api}
              rowId={row._id}
              form={form}
              setForm={setForm}
            />
            {row.component === 'sheet' && (
              <div className="col-span-2 border border-cyan-200 bg-cyan-50/50 rounded-md p-3 space-y-2" data-testid="materials-audit-edit-sheet-coverage">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-cyan-800 font-bold">📐 Sheet coverage · /mtr ↔ /m² conversion</span>
                  <span className="text-[10px] text-slate-500 normal-case tracking-normal">Enter the sheet&apos;s cover width to auto-derive the /m² rate for reports &amp; quotes.</span>
                </div>
                <div className="grid grid-cols-3 gap-3 items-end">
                  <label>
                    <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Cover width (mm)</span>
                    <input
                      type="number" step="1" min="0"
                      value={form.cover_width_mm || 0}
                      onChange={(e) => setForm((f) => ({ ...f, cover_width_mm: Number(e.target.value) || 0 }))}
                      placeholder="762"
                      data-testid="materials-audit-edit-cover_width_mm"
                      className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-cyan-400 tabular-nums"
                    />
                  </label>
                  <div className="text-[11px] text-slate-600 tabular-nums">
                    <div>Materials rate <b className="text-slate-900">${(form.materials_rate || 0).toFixed(2)} /mtr</b></div>
                    <div className="mt-0.5">
                      Derived rate{' '}
                      {form.cover_width_mm > 0 ? (
                        <b className="text-emerald-700 text-sm" data-testid="materials-audit-edit-cover-derived-m2">
                          ${((form.materials_rate || 0) / (form.cover_width_mm / 1000)).toFixed(2)} /m²
                        </b>
                      ) : (
                        <span className="italic text-slate-400">set cover width to compute</span>
                      )}
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-500">
                    <div className="uppercase tracking-wider text-slate-500 mb-0.5">Pricing units</div>
                    <div className="flex flex-wrap gap-1">
                      {(form.pricing_units || []).map((u) => (
                        <span key={u} className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800 border border-cyan-300 tabular-nums">{u}</span>
                      ))}
                      {(!form.pricing_units || form.pricing_units.length === 0) && (
                        <span className="italic text-slate-400">defaults to /mtr on save</span>
                      )}
                    </div>
                    <div className="mt-1 text-[10px] text-slate-500">Operator picks LM or m² on each quote line.</div>
                  </div>
                </div>
              </div>
            )}
              </div>
            </div>
          )}

          {/* More details — per-component meta schema */}
          <div className="border-t border-slate-200 pt-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2 flex items-center gap-1">
              <Sparkles size={11} className="text-orange-600" /> More details · {row.component}
            </div>
            {(() => {
              const hiddenList = Array.isArray(form.meta._hidden) ? form.meta._hidden : [];
              const hideMetaKey = (k) => setForm((f) => ({
                ...f,
                meta: {
                  ...f.meta,
                  _hidden: Array.from(new Set([...(Array.isArray(f.meta._hidden) ? f.meta._hidden : []), k])),
                },
              }));
              const unhideMetaKey = (k) => setForm((f) => ({
                ...f,
                meta: {
                  ...f.meta,
                  _hidden: (Array.isArray(f.meta._hidden) ? f.meta._hidden : []).filter((x) => x !== k),
                },
              }));
              const visibleSchemaFields = schemaFields.filter((f) => !hiddenList.includes(f.key));
              const visibleCustomKeys = Object.keys(form.meta).filter((k) =>
                k !== '_hidden' && !schemaFields.some((f) => f.key === k) && !hiddenList.includes(k)
              );
              const hiddenFieldLabels = hiddenList.map((k) => {
                const spec = schemaFields.find((f) => f.key === k);
                return { key: k, label: spec?.label || k };
              });
              return (
                <>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {visibleSchemaFields.map((f) => (
                      <MetaField
                        key={f.key}
                        spec={f}
                        value={form.meta[f.key]}
                        onChange={(v) => setMeta(f.key, v)}
                        onHide={() => hideMetaKey(f.key)}
                      />
                    ))}
                    {visibleCustomKeys.map((k) => (
                      <MetaField key={k} spec={{ key: k, label: k, type: 'text' }} value={form.meta[k]} onChange={(v) => setMeta(k, v)} custom onRemove={() => {
                        const next = { ...form.meta };
                        delete next[k];
                        setForm((f) => ({ ...f, meta: next }));
                      }} />
                    ))}
                  </div>
                  {hiddenFieldLabels.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="materials-audit-edit-meta-hidden-strip">
                      <span className="text-[9px] text-slate-500 uppercase tracking-wider self-center">Not required for this row:</span>
                      {hiddenFieldLabels.map((h) => (
                        <button
                          key={h.key}
                          type="button"
                          onClick={() => unhideMetaKey(h.key)}
                          data-testid={`materials-audit-edit-meta-unhide-${h.key}`}
                          className="text-[10px] px-2 py-0.5 rounded-full border border-slate-300 bg-slate-50 text-slate-600 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700"
                          title="Re-enable this field"
                        >
                          {h.label} · re-enable
                        </button>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
            <div className="mt-2 flex items-center gap-2">
              <input
                type="text"
                value={customKey}
                onChange={(e) => setCustomKey(e.target.value)}
                placeholder="e.g. thickness_mm"
                data-testid="materials-audit-edit-meta-custom-key"
                className="text-[10px] border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400"
              />
              <button
                type="button"
                onClick={addCustomField}
                data-testid="materials-audit-edit-meta-add"
                disabled={!customKey.trim()}
                className="text-[10px] px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1"
              >
                + Add custom field
              </button>
            </div>
          </div>

          {/* ─── RECIPE SECTION — build or edit inline ─── */}
          <div className="border-t-2 border-slate-200 pt-4 mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-600 font-bold flex items-center gap-1.5">
                <ChefHat size={13} className="text-emerald-600" /> Recipe
                {hasRecipe && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200 normal-case tracking-normal">
                    {row.recipe_entries} entr{row.recipe_entries === 1 ? 'y' : 'ies'}
                  </span>
                )}
              </div>
              {hasRecipe && (
                <button
                  type="button"
                  onClick={simplifyToLumpSum}
                  disabled={recipeBusy}
                  data-testid="materials-audit-edit-simplify-lump-sum"
                  className="text-[10px] px-2 py-1 rounded border border-rose-300 bg-white hover:bg-rose-50 text-rose-700 hover:text-rose-800 inline-flex items-center gap-1 disabled:opacity-50"
                  title="Delete recipe and revert to simple parent-fields row"
                >
                  × Simplify to lump-sum
                </button>
              )}
            </div>
            {hasRecipe ? (
              <div className="border border-slate-200 rounded-md bg-slate-50/50 overflow-hidden" data-testid="materials-audit-edit-recipe-embed">
                <RecipeEditorPage rowId={row._id} embedded onClose={() => {}} />
              </div>
            ) : (
              <div className="border border-dashed border-slate-300 rounded-md bg-slate-50 p-4 text-center">
                <div className="text-xs text-slate-600 mb-2">
                  This row is a lump-sum item. Convert to a recipe if it&apos;s actually a composite (e.g. clip + screws, mortar + pointing).
                </div>
                <button
                  type="button"
                  onClick={buildRecipe}
                  disabled={recipeBusy}
                  data-testid="materials-audit-edit-build-recipe"
                  className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5 font-semibold"
                >
                  {recipeBusy && <Loader2 size={12} className="animate-spin" />}
                  <ChefHat size={12} /> Build recipe for this row
                </button>
              </div>
            )}
          </div>
          </div>{/* /labour_only hide wrapper */}
        </div>

        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-[10px] text-slate-500 italic">
              Changes autosave · press <span className="font-semibold text-slate-700">Save & Close</span> to finish{nameChanged ? ' (runs cascade rename)' : ''}
            </div>
            {/* Move-to-component reclassifier — one-click reassign to a
                different component (e.g. moving Aluminium Spinaway from
                misc to other). Confirms before firing since the change
                also updates the CR mirror. */}
            {availableComponents.length > 1 && (
              <div className="flex items-center gap-1 text-[10px] text-slate-500 border-l border-slate-200 pl-3">
                <span className="uppercase tracking-wider">Move to:</span>
                <select
                  value={(row.component || '').toLowerCase()}
                  onChange={async (e) => {
                    const target = e.target.value;
                    const current = (row.component || '').toLowerCase();
                    if (!target || target === current) return;
                    if (!window.confirm(`Move "${row.name}" from ${current} → ${target}?\n\nThis updates both the catalogue master and the legacy mirror.`)) {
                      // Reset the select to the current component by relying on React re-render.
                      return;
                    }
                    try {
                      await api.post(`/materials-audit/rows/${row._id}/move-component`, { target_component: target });
                      toast.success(`Moved to ${target}`);
                      onSaved && onSaved();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Move failed');
                    }
                  }}
                  data-testid="materials-audit-edit-move-component"
                  className="text-[11px] border border-slate-300 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-orange-400 normal-case tracking-normal"
                >
                  {availableComponents.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={handleClose} data-testid="materials-audit-edit-close" className="text-xs px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50">Close</button>
            <button onClick={save} disabled={saving} data-testid="materials-audit-edit-save" className="text-xs px-3 py-1.5 rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1">
              {saving && <Loader2 size={12} className="animate-spin" />} {nameChanged ? 'Save & Rename Cascade' : 'Save & Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function AutosaveIndicator({ status, lastSavedAt }) {
  const [_, setTick] = useState(0);
  useEffect(() => {
    if (status !== 'saved' || !lastSavedAt) return;
    const t = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, [status, lastSavedAt]);
  const map = {
    idle:            { text: '', className: '' },
    dirty:           { text: 'Saving…',                 className: 'bg-amber-50 text-amber-700 border-amber-200' },
    saving:          { text: 'Saving…',                 className: 'bg-amber-50 text-amber-700 border-amber-200' },
    saved:           { text: lastSavedAt ? `Saved · ${relativeAgo(lastSavedAt)}` : 'Saved', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    'rename-pending':{ text: 'Rename pending · Save & Close to commit', className: 'bg-sky-50 text-sky-700 border-sky-200' },
    error:           { text: 'Save error — try again',  className: 'bg-rose-50 text-rose-700 border-rose-200' },
  };
  const cfg = map[status] || map.idle;
  if (!cfg.text) return null;
  return (
    <span
      data-testid="materials-audit-edit-autosave-status"
      data-status={status}
      className={`text-[10px] px-2 py-0.5 rounded border ${cfg.className} whitespace-nowrap`}
    >
      {cfg.text}
    </span>
  );
}

function relativeAgo(when) {
  const s = Math.max(1, Math.round((Date.now() - when.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return `${h}h ago`;
}


function NotRequiredChip({ label, onEnable, testId }) {
  return (
    <div className="flex items-center gap-2 text-[10px] text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded px-2 py-1.5" data-testid={testId}>
      <span className="uppercase tracking-wider font-bold text-slate-500">{label}</span>
      <span className="text-slate-400 italic">not required</span>
      <button
        type="button"
        onClick={onEnable}
        data-testid={`${testId}-enable`}
        className="ml-auto text-[9px] px-1.5 py-0.5 rounded border border-slate-300 bg-white text-slate-600 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700"
      >
        Show anyway
      </button>
    </div>
  );
}


function MetaField({ spec, value, onChange, custom, onRemove, onHide }) {
  const label = spec.label || spec.key;
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 flex items-center justify-between">
        <span>{label}{custom && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-sky-100 text-sky-700 normal-case tracking-normal">custom</span>}</span>
        {custom ? (
          <button onClick={onRemove} className="text-[9px] text-rose-500 hover:text-rose-700 normal-case tracking-normal">remove</button>
        ) : onHide ? (
          <button
            type="button"
            onClick={onHide}
            data-testid={`materials-audit-edit-meta-${spec.key}-hide`}
            title="Mark as not required for this row"
            className="text-[9px] normal-case tracking-normal font-normal text-slate-400 hover:text-rose-600"
          >× not required</button>
        ) : null}
      </span>
      {spec.type === 'bool' ? (
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={`materials-audit-edit-meta-${spec.key}`} />
      ) : spec.options ? (
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={`materials-audit-edit-meta-${spec.key}`} className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400 bg-white">
          <option value="">—</option>
          {spec.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      ) : (
        <input
          type={spec.type === 'number' ? 'number' : 'text'}
          step={spec.type === 'number' ? '0.01' : undefined}
          value={value ?? ''}
          onChange={(e) => onChange(spec.type === 'number' ? Number(e.target.value) : e.target.value)}
          data-testid={`materials-audit-edit-meta-${spec.key}`}
          className="w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400"
        />
      )}
    </label>
  );
}


function PreviewCalcModal({ api, row, onClose }) {
  const [qty, setQty] = useState(10);
  const [length, setLength] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const runPreview = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/materials-audit/preview/${row._id}?qty=${qty}&length=${length}`);
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, row._id, qty, length]);
  useEffect(() => { runPreview(); }, [runPreview]);

  const openPdf = () => {
    // The api client has JWT + baseURL. Preview PDF uses the same auth.
    // Easiest: fetch as blob then open in new tab.
    (async () => {
      try {
        const resp = await api.get(`/materials-audit/preview-pdf/${row._id}?qty=${qty}&length=${length}`, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
      } catch (err) {
        toast.error(err?.response?.data?.detail || err.message);
      }
    })();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="materials-audit-preview-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">Calc preview — {row.name}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>

        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-1.5 text-xs">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Qty</span>
            <input type="number" value={qty} step="0.1" onChange={(e) => setQty(Number(e.target.value))} data-testid="materials-audit-preview-qty" className="w-20 text-xs border border-slate-300 rounded px-2 py-1 text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-orange-400" />
          </label>
          <label className="flex items-center gap-1.5 text-xs">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Length (m)</span>
            <input type="number" value={length} step="0.1" onChange={(e) => setLength(Number(e.target.value))} data-testid="materials-audit-preview-length" className="w-20 text-xs border border-slate-300 rounded px-2 py-1 text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-orange-400" />
          </label>
          <button
            onClick={openPdf}
            data-testid="materials-audit-preview-open-pdf"
            className="ml-auto inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100"
          >
            <FileDown size={12} /> Open as PDF
          </button>
        </div>

        <div className="p-4 overflow-y-auto space-y-4">
          {loading || !data ? (
            <div className="flex items-center justify-center py-8 text-slate-500 text-xs">
              <Loader2 size={16} className="animate-spin mr-2" /> Calculating…
            </div>
          ) : (
            <>
              {data.agreement && (
                <div className={`rounded p-2 border text-xs ${data.agreement.within_tol ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`} data-testid="materials-audit-preview-agreement">
                  {data.agreement.within_tol ? '✓' : '⚠'} Recipe fanout total drifts <b>{data.agreement.drift_pct}%</b> from the catalogue-legacy total. {data.agreement.within_tol ? 'Within tolerance.' : 'Investigate before shipping this row.'}
                </div>
              )}

              {/* Legacy path */}
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <div className="px-3 py-1.5 bg-slate-100 text-[10px] uppercase tracking-wider font-bold text-slate-600">
                  Legacy catalogue-row math (driver: {data.legacy.driver})
                </div>
                <div className="p-3 text-xs" data-testid="materials-audit-preview-legacy">
                  <div className="grid grid-cols-4 gap-2 text-center mb-2">
                    <StatCell label="Driver" value={`${data.legacy.driver_value}`} />
                    <StatCell label="Ratio × driver" value={`${data.legacy.ratio} × ${data.legacy.driver_value}`} />
                    <StatCell label="Material qty" value={`${data.legacy.material_qty} ${data.legacy.unit}`} accent />
                    <StatCell label="Line total" value={`$${data.legacy.line_total.toFixed(2)}`} accent="orange" />
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {data.legacy.supplier || '—'} · {data.legacy.supplier_code || '—'} · rate ${data.legacy.rate}
                  </div>
                </div>
              </div>

              {/* Fanout path */}
              {data.fanout && (
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <div className="px-3 py-1.5 bg-slate-100 text-[10px] uppercase tracking-wider font-bold text-slate-600 flex items-center justify-between">
                    <span>Recipe fanout ({data.fanout.entries.length} entries)</span>
                    <span className="text-slate-800">Total <b>${data.fanout.recipe_total.toFixed(2)}</b></span>
                  </div>
                  <table className="w-full text-xs" data-testid="materials-audit-preview-fanout">
                    <thead className="bg-slate-50">
                      <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                        <th className="px-2 py-1 text-left">Product</th>
                        <th className="px-2 py-1 text-left">Supplier</th>
                        <th className="px-2 py-1 text-left">Code</th>
                        <th className="px-2 py-1 text-right">Ratio</th>
                        <th className="px-2 py-1 text-right">Qty</th>
                        <th className="px-2 py-1 text-right">Rate</th>
                        <th className="px-2 py-1 text-right">Line $</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.fanout.entries.map((e, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-2 py-1">{e.product_name}{e.is_stock && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-slate-200 text-slate-600 font-bold">STOCK</span>}</td>
                          <td className="px-2 py-1">{e.supplier || '—'}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{e.supplier_code || '—'}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{e.ratio}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{e.material_qty} {e.unit}</td>
                          <td className="px-2 py-1 text-right tabular-nums">${e.rate.toFixed(2)}</td>
                          <td className="px-2 py-1 text-right tabular-nums font-bold">${e.line_total.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-3 py-2 border-t border-slate-100 bg-slate-50">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Per-supplier subtotals</div>
                    <div className="flex flex-wrap gap-2">
                      {data.fanout.by_supplier.map((s) => (
                        <div key={s.supplier} className="text-xs bg-white border border-slate-200 rounded px-2 py-1">
                          <b>{s.supplier}</b> · {s.line_count} line{s.line_count === 1 ? '' : 's'} · <span className="font-mono">${s.subtotal.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {!data.fanout && (
                <div className="text-xs text-slate-500 italic border border-dashed border-slate-300 rounded p-3 text-center">
                  No recipe attached — legacy path only. (Add via the Recipe button if this row should fan out.)
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function BulkAssignModal({ api, rowIds, onClose, onSaved }) {
  const [supplier, setSupplier] = useState('');
  const [code, setCode] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!supplier.trim()) { toast.error('Supplier is required'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/materials-audit/bulk-assign-supplier', {
        row_ids: rowIds, supplier, supplier_code: code,
      });
      toast.success(`Updated ${data.updated} rows`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="materials-audit-bulk-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">Bulk assign supplier to {rowIds.length} row{rowIds.length === 1 ? '' : 's'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3 text-xs">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Supplier *</span>
            <input value={supplier} onChange={(e) => setSupplier(e.target.value)} data-testid="materials-audit-bulk-supplier" className="mt-1 w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Supplier code (optional)</span>
            <input value={code} onChange={(e) => setCode(e.target.value)} data-testid="materials-audit-bulk-code" className="mt-1 w-full text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400" />
            <span className="text-[10px] text-slate-400 italic">If set, applies the same code to all selected rows.</span>
          </label>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="materials-audit-bulk-save" className="text-xs px-3 py-1.5 rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1">
            {saving && <Loader2 size={12} className="animate-spin" />} Apply
          </button>
        </div>
      </div>
    </div>
  );
}


function AutoUnitBanner({ suggestion }) {
  return (
    <div className="mx-3 my-2 p-2 rounded border border-sky-200 bg-sky-50 text-xs flex items-center gap-2" data-testid="materials-audit-auto-unit-banner">
      <Sparkles size={12} className="text-sky-600" />
      <span className="text-slate-700">
        <b>{suggestion.majority_count}</b> of <b>{suggestion.total}</b> rows use <b>/{suggestion.majority_unit}</b>.
        Outliers: {Object.entries(suggestion.outliers).map(([u, n]) => `/${u} (${n})`).join(', ')}.
      </span>
    </div>
  );
}


function AutoFillPanel({ api, component, onApplied }) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [skipLow, setSkipLow] = useState(false);
  const [skipIds, setSkipIds] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setSkipIds(new Set());
      try {
        const { data } = await api.post('/materials-audit/infer-meta', {
          component, dry_run: true, only_fill_empty: true,
        });
        if (!cancelled) setProposals(data?.proposals || []);
      } catch (err) {
        if (!cancelled) toast.error(`Auto-fill preview failed: ${err?.response?.data?.detail || err.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, component, reloadTick]);

  const applicable = proposals.filter((p) => p.would_change);
  const partial    = applicable.filter((p) => Object.values(p.confidence || {}).some((c) => c !== 'HIGH'));
  const highOnly   = applicable.filter((p) => Object.values(p.confidence || {}).every((c) => c === 'HIGH'));
  const selected   = applicable.filter((p) => !skipIds.has(p.row_id) && (!skipLow || Object.values(p.confidence || {}).every((c) => c === 'HIGH')));

  const apply = async () => {
    if (selected.length === 0) return;
    setApplying(true);
    try {
      const { data } = await api.post('/materials-audit/infer-meta', {
        component,
        dry_run: false,
        only_fill_empty: true,
        row_ids: selected.map((p) => p.row_id),
      });
      toast.success(`Auto-filled ${data.applied} rows from name parser`);
      setReloadTick((n) => n + 1);
      onApplied?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-3 my-2 p-2 rounded border border-slate-200 bg-slate-50 text-xs text-slate-500 flex items-center gap-2" data-testid="materials-audit-autofill-panel">
        <Loader2 size={12} className="animate-spin" /> Scanning row names for auto-fillable meta…
      </div>
    );
  }
  if (applicable.length === 0) {
    return null;  // nothing to backfill for this component
  }

  return (
    <div className="mx-3 my-2 rounded border border-emerald-200 bg-emerald-50/50" data-testid="materials-audit-autofill-panel">
      <div className="p-2 flex items-center gap-2 flex-wrap">
        <Sparkles size={12} className="text-emerald-600" />
        <span className="text-xs text-slate-700">
          <b>{applicable.length}</b> of <b>{proposals.length}</b> rows can be auto-filled from name.
          {partial.length > 0 && <span className="text-slate-500"> · {highOnly.length} high-confidence · {partial.length} partial</span>}
        </span>
        <label className="flex items-center gap-1 text-[10px] text-slate-600 ml-1">
          <input
            type="checkbox"
            checked={skipLow}
            onChange={(e) => setSkipLow(e.target.checked)}
            data-testid="materials-audit-autofill-skip-low"
          />
          Skip partial (only apply HIGH-confidence rows)
        </label>
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          data-testid="materials-audit-autofill-toggle"
          className="text-[10px] px-2 py-0.5 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
        >
          {expanded ? 'Hide details' : `Preview ${applicable.length} proposals`}
        </button>
        <button
          type="button"
          onClick={apply}
          disabled={applying || selected.length === 0}
          data-testid="materials-audit-autofill-apply"
          className="ml-auto inline-flex items-center gap-1 text-xs px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {applying && <Loader2 size={12} className="animate-spin" />}
          Apply to {selected.length} rows
        </button>
      </div>
      {expanded && (
        <div className="border-t border-emerald-200 max-h-72 overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-emerald-100/60 sticky top-0">
              <tr className="text-[9px] uppercase tracking-wider text-slate-600">
                <th className="px-2 py-1 text-left w-6"></th>
                <th className="px-2 py-1 text-left">Row</th>
                <th className="px-2 py-1 text-left">Will add</th>
                <th className="px-2 py-1 text-left w-24">Confidence</th>
              </tr>
            </thead>
            <tbody>
              {applicable.map((p) => {
                const skipped = skipIds.has(p.row_id);
                const isLow = Object.values(p.confidence || {}).some((c) => c !== 'HIGH');
                const effectivelySkipped = skipped || (skipLow && isLow);
                return (
                  <tr key={p.row_id} className={effectivelySkipped ? 'text-slate-400 line-through' : ''}>
                    <td className="px-2 py-1">
                      <input
                        type="checkbox"
                        checked={!effectivelySkipped}
                        onChange={() => {
                          const next = new Set(skipIds);
                          if (skipIds.has(p.row_id)) next.delete(p.row_id); else next.add(p.row_id);
                          setSkipIds(next);
                        }}
                        disabled={skipLow && isLow}
                        data-testid={`materials-audit-autofill-row-${p.row_id}`}
                      />
                    </td>
                    <td className="px-2 py-1 truncate max-w-xs" title={p.name}>{p.name}</td>
                    <td className="px-2 py-1">
                      {p.changed_keys.map((k) => {
                        const v = k === 'unit' ? p.patch.unit : p.patch.meta?.[k];
                        return (
                          <span key={k} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded bg-white border border-emerald-200 text-[10px]">
                            <span className="text-slate-500">{k}:</span> <span className="font-mono text-slate-800">{String(v)}</span>
                          </span>
                        );
                      })}
                    </td>
                    <td className="px-2 py-1">
                      {isLow ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">partial</span>
                      ) : (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">high</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function StatCell({ label, value, accent }) {
  const cls =
    accent === 'orange'
      ? 'text-orange-700 font-bold'
      : accent
      ? 'text-slate-900 font-bold'
      : 'text-slate-800';
  return (
    <div className="rounded bg-slate-50 border border-slate-100 p-1.5">
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xs tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
